import React, { useState, useEffect } from 'react';
import { 
  obtenerEstadisticasUsuarios, 
  obtenerResumenWavespeed, 
  obtenerConsumoCreditos, 
  obtenerAlertas,
  obtenerPagosResumen
} from '../../services/dashboard';
import '../../styles/admin/AdminDashboard.css';

function AdminDashboard() {
  // Estados
  const [estadisticasUsuarios, setEstadisticasUsuarios] = useState(null);
  const [resumenWavespeed, setResumenWavespeed] = useState(null);
  const [consumoCreditos, setConsumoCreditos] = useState(null);
  const [alertas, setAlertas] = useState(null);
  const [pagosResumen, setPagosResumen] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [mesSeleccionado, setMesSeleccionado] = useState('todos');

  // Generar opciones de meses (últimos 12 meses + "Todos")
  const generarOpcionesMeses = () => {
    const opciones = [{ value: 'todos', label: '📅 Todos los períodos' }];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const label = date.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' });
      opciones.push({ value, label });
    }
    return opciones;
  };

  const opcionesMeses = generarOpcionesMeses();

  // Función para cargar datos globales (sin filtro)
  const cargarDatosGlobales = async () => {
    try {
      const [usuarios, wavespeed, alertasData, pagos] = await Promise.all([
        obtenerEstadisticasUsuarios(),
        obtenerResumenWavespeed(),
        obtenerAlertas(),
        obtenerPagosResumen()
      ]);
      setEstadisticasUsuarios(usuarios);
      setResumenWavespeed(wavespeed);
      setAlertas(alertasData);
      setPagosResumen(pagos);
    } catch (err) {
      console.error('Error cargando datos globales:', err);
      throw err;
    }
  };

  // Función para cargar consumo de créditos (cambia con el filtro)
  const cargarConsumoCreditos = async (mes) => {
    try {
      const mesParam = mes === 'todos' ? null : mes;
      const consumo = await obtenerConsumoCreditos(mesParam);
      setConsumoCreditos(consumo);
    } catch (err) {
      console.error('Error cargando consumo de créditos:', err);
      throw err;
    }
  };

  // Cargar todos los datos
  const cargarDashboard = async () => {
    setLoading(true);
    setError('');
    try {
      await cargarDatosGlobales();
      await cargarConsumoCreditos(mesSeleccionado);
    } catch (err) {
      console.error('Error cargando dashboard:', err);
      setError('Error al cargar los datos. Reintenta.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargarDashboard();
  }, [mesSeleccionado]);

  const handleCambioMes = (e) => {
    setMesSeleccionado(e.target.value);
  };

  // Formatear texto del mes seleccionado
  const getTextoMes = () => {
    if (mesSeleccionado === 'todos') return 'Todos los períodos';
    const [year, month] = mesSeleccionado.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1, 1);
    return date.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="admin-dashboard-loading">
        <div className="spinner"></div>
        <p>Cargando dashboard...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-dashboard-error">
        <p>{error}</p>
        <button onClick={cargarDashboard}>Reintentar</button>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      {/* HEADER */}
      <div className="admin-dashboard-header">
        <div>
          <h1>📊 Dashboard</h1>
          <p>Resumen ejecutivo del sistema</p>
        </div>
      </div>

      {/* ============================================ */}
      {/* SECCIÓN 1: MÉTRICAS GLOBALES (NUNCA CAMBIAN) */}
      {/* ============================================ */}
      <div className="dashboard-section">
        <h2 className="section-title">📌 Métricas Globales</h2>
        <div className="cards-grid cards-grid-4">
          <div className="dashboard-card card-blue">
            <div className="card-header">
              <span className="card-icon">👥</span>
              <span className="card-label">Usuarios Totales</span>
            </div>
            <div className="card-value">{estadisticasUsuarios?.total_usuarios || 0}</div>
            <div className="card-sub">Registrados históricamente</div>
          </div>
          <div className="dashboard-card card-green">
            <div className="card-header">
              <span className="card-icon">✅</span>
              <span className="card-label">Usuarios Activos</span>
            </div>
            <div className="card-value">{estadisticasUsuarios?.usuarios_activos || 0}</div>
            <div className="card-sub">
              {estadisticasUsuarios?.total_usuarios > 0 
                ? `${Math.round((estadisticasUsuarios.usuarios_activos / estadisticasUsuarios.total_usuarios) * 100)}% del total`
                : '0% del total'}
            </div>
          </div>
          <div className="dashboard-card card-gold">
            <div className="card-header">
              <span className="card-icon">💰</span>
              <span className="card-label">Ingresos Totales</span>
            </div>
            <div className="card-value">
              ${pagosResumen?.ingresos_totales?.toFixed(2) || '0.00'}
            </div>
            <div className="card-sub">Histórico de todos los pagos</div>
          </div>
          <div className="dashboard-card card-purple">
            <div className="card-header">
              <span className="card-icon">⚡</span>
              <span className="card-label">Saldo Wavespeed</span>
            </div>
            <div className="card-value">
              ${resumenWavespeed?.saldo?.toFixed(2) || '0.00'}
            </div>
            <div className="card-sub">
              Nivel: {resumenWavespeed?.nivel || 'No disponible'}
            </div>
          </div>
        </div>
      </div>

      {/* ============================================ */}
      {/* SECCIÓN 2: DETALLE DE USUARIOS (SIEMPRE GLOBAL) */}
      {/* ============================================ */}
      <div className="dashboard-section">
        <h2 className="section-title">👥 Detalle de Usuarios</h2>
        <div className="cards-grid cards-grid-4">
          <div className="dashboard-card card-blue">
            <div className="card-header">
              <span className="card-icon">👥</span>
              <span className="card-label">Total</span>
            </div>
            <div className="card-value">{estadisticasUsuarios?.total_usuarios || 0}</div>
          </div>
          <div className="dashboard-card card-green">
            <div className="card-header">
              <span className="card-icon">✅</span>
              <span className="card-label">Activos</span>
            </div>
            <div className="card-value">{estadisticasUsuarios?.usuarios_activos || 0}</div>
            <div className="card-sub">
              {estadisticasUsuarios?.total_usuarios > 0 
                ? `${Math.round((estadisticasUsuarios.usuarios_activos / estadisticasUsuarios.total_usuarios) * 100)}%`
                : '0%'}
            </div>
          </div>
          <div className="dashboard-card card-gold">
            <div className="card-header">
              <span className="card-icon">⭐</span>
              <span className="card-label">Con Créditos</span>
            </div>
            <div className="card-value">{estadisticasUsuarios?.usuarios_con_creditos || 0}</div>
          </div>
          <div className="dashboard-card card-red">
            <div className="card-header">
              <span className="card-icon">🚫</span>
              <span className="card-label">Sin Créditos</span>
            </div>
            <div className="card-value">{estadisticasUsuarios?.usuarios_sin_creditos || 0}</div>
          </div>
        </div>
      </div>

      {/* ============================================ */}
      {/* SECCIÓN 3: FILTRO + MÉTRICAS DEL PERIODO */}
      {/* ============================================ */}
      <div className="dashboard-section">
        <div className="section-header-with-filter">
          <h2 className="section-title">
            📈 Métricas del Período
          </h2>
          <div className="admin-dashboard-filtro">
            <label htmlFor="mesFiltro">📅 Filtrar por:</label>
            <select
              id="mesFiltro"
              value={mesSeleccionado}
              onChange={handleCambioMes}
              className="filtro-select"
            >
              {opcionesMeses.map(opcion => (
                <option key={opcion.value} value={opcion.value}>
                  {opcion.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        <p className="periodo-indicador">
          Mostrando datos para: <strong className="periodo-activo">{getTextoMes()}</strong>
        </p>
        <div className="cards-grid cards-grid-4">
          <div className="dashboard-card card-cyan">
            <div className="card-header">
              <span className="card-icon">🆕</span>
              <span className="card-label">Nuevos Usuarios</span>
            </div>
            <div className="card-value">{estadisticasUsuarios?.nuevos_usuarios_mes || 0}</div>
            <div className="card-sub">
              {mesSeleccionado === 'todos' 
                ? 'Total de registros'
                : 'Registrados en este mes'}
            </div>
          </div>
          <div className="dashboard-card card-green">
            <div className="card-header">
              <span className="card-icon">💵</span>
              <span className="card-label">Ingresos del Período</span>
            </div>
            <div className="card-value">
              ${pagosResumen?.ingresos_mes_actual?.toFixed(2) || '0.00'}
            </div>
            <div className="card-sub">
              {pagosResumen?.total_pagos_completados_mes || 0} pagos completados
            </div>
          </div>
          <div className="dashboard-card card-orange">
            <div className="card-header">
              <span className="card-icon">📊</span>
              <span className="card-label">Crecimiento</span>
            </div>
            <div className="card-value">
              <span className={pagosResumen?.evolucion_porcentual >= 0 ? 'crecimiento-positivo' : 'crecimiento-negativo'}>
                {pagosResumen?.evolucion_porcentual || 0}%
              </span>
            </div>
            <div className="card-sub">
              vs período anterior
            </div>
          </div>
          <div className="dashboard-card card-purple">
            <div className="card-header">
              <span className="card-icon">🔥</span>
              <span className="card-label">Créditos Consumidos</span>
            </div>
            <div className="card-value">{consumoCreditos?.total_creditos_consumidos || 0}</div>
            <div className="card-sub">
              {consumoCreditos?.usuarios_activos_en_consumo || 0} usuarios activos
            </div>
          </div>
        </div>
      </div>

      {/* ============================================ */}
      {/* SECCIÓN 4: CONSUMO DETALLADO (Cambia con el filtro) */}
      {/* ============================================ */}
      <div className="dashboard-section">
        <h2 className="section-title">
          📊 Consumo Detallado - <span className="periodo-activo">{getTextoMes()}</span>
        </h2>
        <div className="cards-grid cards-grid-2">
          <div className="dashboard-card card-pink">
            <div className="card-header">
              <span className="card-icon">🖼️</span>
              <span className="card-label">Imágenes Generadas</span>
            </div>
            <div className="card-value">{consumoCreditos?.total_imagenes || 0}</div>
          </div>
          <div className="dashboard-card card-indigo">
            <div className="card-header">
              <span className="card-icon">👤</span>
              <span className="card-label">Usuarios con Consumo</span>
            </div>
            <div className="card-value">{consumoCreditos?.usuarios_activos_en_consumo || 0}</div>
          </div>
        </div>
      </div>

      {/* ============================================ */}
      {/* SECCIÓN 5: ALERTAS (Siempre visible) */}
      {/* ============================================ */}
      <div className="dashboard-section">
        <h2 className="section-title">🔔 Alertas del Sistema</h2>
        {alertas?.total_alertas === 0 ? (
          <div className="no-alertas">✅ Sin alertas pendientes</div>
        ) : (
          <div className="alertas-container">
            {alertas?.creditos_bajos?.length > 0 && (
              <div className="alerta alerta-warning">
                <div className="alerta-header">
                  <span className="alerta-icon">⚠️</span>
                  <h4>Usuarios con créditos bajos</h4>
                  <span className="alerta-badge">{alertas.creditos_bajos.length}</span>
                </div>
                <ul className="alerta-lista">
                  {alertas.creditos_bajos.map((u) => (
                    <li key={u.usuario_id}>
                      <strong>{u.nombre}</strong> ({u.email}) - {u.creditos} créditos
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminDashboard;